// import { configure } from "../config.js";
import React, { useState, useEffect } from "react";
import {
  usePasswordless,
  useAwaitableState,
  useLocalUserCache,
} from "amazon-cognito-passwordless-auth/react";
import { Signin } from "./SignIn";
import { FidoReminder } from "./FidoReminder";
import { getRelativeTime } from "utils/date";
import { Trans, useTranslation } from "react-i18next";
import { useLanguageQuery } from "utils/hooks";

interface CustomBrand {
  backgroundImageUrl?: string;
  customerName?: string;
  customerLogoUrl?: string;
}

const FlexContainer = (props: {
  children: React.ReactNode;
  brand?: CustomBrand;
}) => {
  return (
    <div className="h-screen w-full flex justify-items-center items-center">
      {props.brand?.backgroundImageUrl && (
        <img
          src={props.brand.backgroundImageUrl}
          className="passwordless-main-background-image"
        />
      )}
      <div className="passwordless-card-container">
        {props.brand?.customerLogoUrl && (
          <img
            src={props.brand.customerLogoUrl}
            className="passwordless-customer-logo"
          />
        )}
        {props.brand?.customerName && (
          <div className="passwordless-text-center passwordless-customer-name">
            {props.brand.customerName}
          </div>
        )}
        {props.children}
      </div>
    </div>
  );
};

export const Passwordless = ({
  brand,
  children,
}: {
  brand?: CustomBrand;
  children?: React.ReactNode;
}) => {
  const { signInStatus, signingInStatus, tokens } = usePasswordless();

  const [showUsernameInput, setShowUsernameInput] = useState<boolean>();
  const { lastSignedInUsers } = useLocalUserCache();

  useLanguageQuery();

  useEffect(() => {
    if (lastSignedInUsers !== undefined) {
      setShowUsernameInput(lastSignedInUsers.length === 0);
    }
  }, [lastSignedInUsers]);

  if (signInStatus === "REFRESHING_SIGN_IN" && children) {
    return <>{children}</>;
  }

  if (
    signInStatus === "CHECKING" ||
    signInStatus === "REFRESHING_SIGN_IN" ||
    !lastSignedInUsers
  ) {
    return (
      <FlexContainer brand={brand}>
        <div className="passwordless-flex">
          <div className="passwordless-loading-spinner" />
          <div>Checking your sign-in status...</div>
        </div>
      </FlexContainer>
    );
  }

  if (signingInStatus === "SIGNING_IN_WITH_LINK") {
    return (
      <FlexContainer brand={brand}>
        <div className="passwordless-flex">
          <div className="passwordless-loading-spinner" />
          <div>Checking the sign-in link...</div>
        </div>
      </FlexContainer>
    );
  }

  if (signingInStatus === "SIGNING_OUT") {
    return (
      <FlexContainer brand={brand}>
        <div className="passwordless-flex">
          <div className="passwordless-loading-spinner" />
          <div>Signing out, please wait...</div>
        </div>
      </FlexContainer>
    );
  }

  if (signInStatus === "SIGNED_IN" && tokens) {
    if (children) return <>{children}</>;
  }

  return <Signin />;
};

function Fido2Recommendation() {
  const { fido2CreateCredential, showAuthenticatorManager, signInStatus } =
    usePasswordless();
  const { currentUser, updateFidoPreference } = useLocalUserCache();
  const [error, setError] = useState<Error>();
  const [status, setStatus] = useState<
    "IDLE" | "STARTING" | "INPUT_NAME" | "COMPLETING" | "COMPLETED"
  >("IDLE");
  useEffect(() => {
    if (status !== "COMPLETED") return;
    const i = setTimeout(reset, 10000);
    return () => clearTimeout(i);
  }, [status]);
  const [friendlyName, setFriendlyName] = useState("");
  const { resolve: resolveFriendlyName } = useAwaitableState(friendlyName);

  function reset() {
    setError(undefined);
    setStatus("IDLE");
    setFriendlyName("");
  }

  useEffect(() => {
    if (showAuthenticatorManager) {
      reset();
    }
  }, [showAuthenticatorManager]);

  if (showAuthenticatorManager) return null;

  const show =
    signInStatus === "SIGNED_IN" &&
    currentUser &&
    (currentUser.useFido === "ASK" || status === "COMPLETED");

  if (!show) return null;

  return (
    <div>
      <div className="">
        {(status === "IDLE" || status === "STARTING") && (
          <FidoReminder
            onReject={() => {
              updateFidoPreference({ useFido: "NO" });
              reset();
            }}
            onAccept={() => {
              setStatus("STARTING");
              fido2CreateCredential({
                friendlyName: "Passkey-1",
              })
                .then(() => {
                  updateFidoPreference({ useFido: "YES" });
                })
                .catch(setError)
                .finally(() => setStatus("COMPLETED"));
            }}
          />
        )}
        {(status === "INPUT_NAME" || status === "COMPLETING") && (
          <form
            className="passwordless-flex"
            onSubmit={(e) => {
              e.preventDefault();
              resolveFriendlyName();
              setStatus("COMPLETING");
              return false;
            }}
          >
            <div className="passwordless-fido-recommendation-text">
              Provide a name for this authenticator, so you can recognize it
              easily later
            </div>
            <input
              className="passwordless-friendly-name-input"
              autoFocus
              placeholder="authenticator name"
              value={friendlyName}
              onChange={(e) => setFriendlyName(e.target.value)}
            />
            <button
              className="passwordless-button passwordless-button-finish"
              type="submit"
              disabled={!friendlyName || status === "COMPLETING"}
            >
              Finish
            </button>
            <div
              className="passwordless-link"
              onClick={() => {
                updateFidoPreference({ useFido: "NO" });
                reset();
              }}
            >
              cancel
            </div>
          </form>
        )}
        {status === "COMPLETED" && (
          <>
            {" "}
            <div className="passwordless-fido-recommendation-text">
              {error
                ? `Failed to activate face or touch unlock: ${error.message}`
                : "Face or touch unlock activated successfully"}
            </div>
            <div onClick={reset} className="passwordless-link">
              schließen
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AuthenticatorsManager() {
  const { t } = useTranslation();
  const {
    fido2CreateCredential,
    fido2Credentials,
    showAuthenticatorManager,
    toggleShowAuthenticatorManager,
    signInStatus,
  } = usePasswordless();
  const { updateFidoPreference } = useLocalUserCache();
  const [error, setError] = useState<Error>();
  const [addingAuthenticatorStatus, setAddingAuthenticatorStatus] = useState<
    "IDLE" | "STARTING" | "INPUT_NAME" | "COMPLETING"
  >("IDLE");
  const [confirmDeleteRowIndex, setConfirmDeleteRowIndex] =
    useState<number>(-1);
  const [friendlyName, setFriendlyName] = useState("");
  const [editFriendlyNameRowIndex, setEditFriendlyNameRowIndex] =
    useState<number>(-1);
  const [editedFriendlyName, setEditedFriendlyName] = useState("");
  const { awaitable: awaitableFriendlyName, resolve: resolveFriendlyName } =
    useAwaitableState(friendlyName);
  const mobileDeviceName = determineMobileDeviceName();
  const [time, setTime] = useState(new Date());
  function reset() {
    setError(undefined);
    setConfirmDeleteRowIndex(-1);
    setEditFriendlyNameRowIndex(-1);
    setAddingAuthenticatorStatus("IDLE");
    setFriendlyName("");
    setEditedFriendlyName("");
  }
  useEffect(() => {
    if (showAuthenticatorManager) {
      reset();
    }
  }, [showAuthenticatorManager]);
  useEffect(() => {
    if (showAuthenticatorManager && signInStatus === "NOT_SIGNED_IN") {
      toggleShowAuthenticatorManager();
    }
  }, [signInStatus, showAuthenticatorManager, toggleShowAuthenticatorManager]);
  useEffect(() => {
    const intervalId = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(intervalId);
  }, []);
  if (!showAuthenticatorManager) return null;
  const status = {
    isAddingAuthenticator: addingAuthenticatorStatus !== "IDLE",
    isDeletingAuthenticator: confirmDeleteRowIndex !== -1,
    isEditingAuthenticator: editFriendlyNameRowIndex !== -1,
  };
  return (
    <div className="passwordless-table">
      {(addingAuthenticatorStatus === "IDLE" ||
        addingAuthenticatorStatus === "STARTING") && (
        <>
          {fido2Credentials?.length === 0 && (
            <div className="passwordless-no-devices-yet">
              <span>{t("authdevices.no_devices")}</span>
              <span>
                <Trans i18nKey="authdevices.create_new" />
              </span>
            </div>
          )}
          {!!fido2Credentials?.length && (
            <table>
              <thead>
                <tr
                  className={
                    editFriendlyNameRowIndex !== -1 ||
                    confirmDeleteRowIndex !== -1
                      ? "passwordless-table-hide-headers"
                      : ""
                  }
                >
                  <th></th>
                  <th className="passwordless-table-col-last-sign-in">
                    {t("authdevices.last_login")}
                  </th>
                  <th className="passwordless-table-col-created-at">
                    {t("authdevices.created_at")}
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {fido2Credentials.map((credential, index) =>
                  editFriendlyNameRowIndex === index ? (
                    <tr key={credential.credentialId}>
                      <td colSpan={4}>
                        <form
                          className="passwordless-edit-friendly-name"
                          onSubmit={(e) => {
                            e.preventDefault();
                            setError(undefined);
                            credential
                              .update({
                                friendlyName: editedFriendlyName,
                              })
                              .then(reset)
                              .catch(setError);
                            return false;
                          }}
                        >
                          <input
                            className="passwordless-friendly-name-input"
                            autoFocus
                            value={editedFriendlyName}
                            onChange={(e) =>
                              setEditedFriendlyName(e.currentTarget.value)
                            }
                          />
                          <button
                            className="passwordless-button passwordless-button-save bg-cyan-600"
                            type="submit"
                            disabled={
                              credential.busy ||
                              !editedFriendlyName ||
                              editedFriendlyName === credential.friendlyName
                            }
                          >
                            Save
                          </button>
                          <button
                            className="passwordless-button passwordless-button-cancel"
                            onClick={() => setEditFriendlyNameRowIndex(-1)}
                            disabled={credential.busy}
                          >
                            Cancel
                          </button>
                        </form>
                      </td>
                    </tr>
                  ) : confirmDeleteRowIndex === index ? (
                    <tr key={credential.credentialId}>
                      <td colSpan={4}>
                        <div className="passwordless-confirm-delete-device">
                          <span>
                            <Trans
                              i18nKey="authdevices.delete.confirmation"
                              values={{ authName: credential.friendlyName }}
                            />
                          </span>
                          <div>
                            <button
                              className="passwordless-button passwordless-button-save bg-cyan-600"
                              onClick={() => {
                                setError(undefined);
                                credential.delete().then(reset).catch(setError);
                              }}
                              disabled={credential.busy}
                            >
                              {t("Ja")}
                            </button>
                            <button
                              className="passwordless-button passwordless-button-cancel"
                              onClick={() => {
                                setError(undefined);
                                setConfirmDeleteRowIndex(-1);
                              }}
                              disabled={credential.busy}
                            >
                              {t("Nein")}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={credential.credentialId}>
                      <td className="passwordless-table-col-friendly-name passwordless-table-cell-ellipsis">
                        <span>
                          <button
                            className="passwordless-friendly-name"
                            onClick={() => {
                              setEditFriendlyNameRowIndex(index);
                              setEditedFriendlyName(credential.friendlyName);
                            }}
                            disabled={
                              credential.busy ||
                              status.isAddingAuthenticator ||
                              status.isEditingAuthenticator ||
                              status.isDeletingAuthenticator
                            }
                          >
                            <svg
                              className="passwordless-edit-icon"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                            >
                              <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" />
                            </svg>
                            {credential.friendlyName}
                          </button>
                        </span>
                      </td>
                      <td className="passwordless-table-col-last-sign-in">
                        {credential.lastSignIn
                          ? getRelativeTime(credential.lastSignIn.getTime())
                          : t("authdevices.never_used")}
                      </td>
                      <td className="passwordless-table-col-created-at">
                        {getRelativeTime(credential.createdAt.getTime())}
                      </td>
                      <td className="passwordless-table-col-delete">
                        <button
                          className="passwordless-button passwordless-button-outlined passwordless-button-delete"
                          onClick={() => {
                            setError(undefined);
                            setConfirmDeleteRowIndex(index);
                          }}
                          disabled={
                            credential.busy ||
                            status.isAddingAuthenticator ||
                            status.isEditingAuthenticator ||
                            status.isDeletingAuthenticator
                          }
                        >
                          <svg
                            className="passwordless-delete-icon"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path d="M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M9,8H11V17H9V8M13,8H15V17H13V8Z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          )}
        </>
      )}
      <div className="passwordless-authenticators-action-row">
        {(addingAuthenticatorStatus === "IDLE" ||
          addingAuthenticatorStatus === "STARTING") && (
          <button
            className="flex items-center gap-x-1.5 justify-center rounded-md bg-cyan-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600 hover:bg-cyan-800"
            onClick={() => {
              setAddingAuthenticatorStatus("STARTING");
              setError(undefined);
              fido2CreateCredential({
                friendlyName: () => {
                  if (mobileDeviceName) return mobileDeviceName;
                  setAddingAuthenticatorStatus("INPUT_NAME");
                  return awaitableFriendlyName();
                },
              })
                .then(() => {
                  updateFidoPreference({ useFido: "YES" });
                  reset();
                })
                .catch(setError)
                .finally(() => setAddingAuthenticatorStatus("IDLE"));
            }}
            disabled={
              addingAuthenticatorStatus === "STARTING" ||
              status.isEditingAuthenticator ||
              status.isDeletingAuthenticator
            }
          >
            {t("authdevices.cta")}
          </button>
        )}
        {(addingAuthenticatorStatus === "INPUT_NAME" ||
          addingAuthenticatorStatus === "COMPLETING") && (
          <form
            className="passwordless-flex"
            onSubmit={(e) => {
              e.preventDefault();
              resolveFriendlyName();
              setAddingAuthenticatorStatus("COMPLETING");
              return false;
            }}
          >
            <div className="passwordless-fido-recommendation-text">
              {t("authdevices.new.provide_name")}
            </div>
            <input
              className="passwordless-friendly-name-input"
              autoFocus
              placeholder={t("authdevices.new.placeholder")}
              value={friendlyName}
              onChange={(e) => setFriendlyName(e.target.value)}
            />
            <button
              className="passwordless-button passwordless-button-finish"
              type="submit"
              disabled={
                !friendlyName || addingAuthenticatorStatus === "COMPLETING"
              }
            >
              {t("authdevices.new.cta")}
            </button>
          </form>
        )}
        <div
          onClick={() => toggleShowAuthenticatorManager()}
          className="passwordless-link"
        >
          {t("authdevices.cancel")}
        </div>
      </div>
      {error && (
        <div className="passwordless-authenticator-error">{error.message}</div>
      )}
    </div>
  );
}

export function Fido2Toast() {
  return (
    <>
      <Fido2Recommendation />
      <AuthenticatorsManager />
    </>
  );
}

function determineMobileDeviceName() {
  const mobileDevices = [
    "Android",
    "webOS",
    "iPhone",
    "iPad",
    "iPod",
    "BlackBerry",
    "Windows Phone",
  ] as const;
  return mobileDevices.find((dev) =>
    navigator.userAgent.match(new RegExp(dev, "i"))
  );
}
